import { config } from '../config';
import { Logger } from '../monitoring';
import { generateCodeChallenge, generateCodeVerifier, generateRandomString } from '../utils/pkce';
import { getSessionStorageItemSafely, removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../utils/storage';
import { peekStoredAuthorizeUrl } from './useRedirectParam';

export type SsoProvider = 'google' | 'intuit' | 'xero';

const SSO_PROVIDERS: SsoProvider[] = ['google', 'intuit', 'xero'];

export const isSsoProvider = (value: string | null): value is SsoProvider => value !== null && (SSO_PROVIDERS as string[]).includes(value);

export const SSO_CODE_VERIFIER_KEY = 'sso_code_verifier';
export const SSO_RETURN_PATH_KEY = 'sso_return_path';
export const SSO_DIRECT_ENTRY_KEY = 'sso_direct_entry';
// Diagnostic only — never read to gate behavior. Unlike SSO_CODE_VERIFIER_KEY, this is
// never removed, so it stays present for the tab's lifetime (sessionStorage is per-tab:
// absent in a genuinely new tab, but copied along on a duplicated/restored tab). Lets us
// tell, after the fact, whether a "missing required parameters" callback error happened in
// the same browsing context that started the flow.
export const SSO_TAB_ID_KEY = 'sso_tab_id';
// Marks a given sso_state as already processed by this browsing context, keyed per-value
// (not a single flag) so a later, distinct SSO attempt in the same tab isn't mistaken for a
// replay of an earlier one. Written once the callback commits to handling an sso_state, before
// dispatching into its error/success branches, so ANY later reload/replay of that exact
// callback URL — same tab, or a duplicated/restored tab that inherited the sessionStorage
// snapshot — can be recognized as stale rather than logged as a bug.
const SSO_HANDLED_KEY_PREFIX = 'sso_callback_handled:';

const ensureSsoTabId = (): string | null => {
  const existing = getSessionStorageItemSafely(SSO_TAB_ID_KEY);
  if (existing) return existing;
  const tabId = generateRandomString(8);
  return setSessionStorageItemSafely(SSO_TAB_ID_KEY, tabId) ? tabId : null;
};

export const wasSsoStateHandled = (ssoState: string): boolean =>
  getSessionStorageItemSafely(`${SSO_HANDLED_KEY_PREFIX}${ssoState}`) === '1';

export const markSsoStateHandled = (ssoState: string): void => {
  setSessionStorageItemSafely(`${SSO_HANDLED_KEY_PREFIX}${ssoState}`, '1');
};

export const startSso = async (provider: SsoProvider, options: { isDirectEntry?: boolean; interactive?: boolean } = {}) => {
  // Written before any fallible awaited work below (e.g. generateCodeChallenge, which needs
  // crypto.subtle) so that if that work throws, redirectSsoError still knows this was a
  // direct-entry attempt and bounces the error back through the caller's /oauth/authorize
  // instead of silently falling back to a bare /sign-in.
  if (options.isDirectEntry) {
    setSessionStorageItemSafely(SSO_DIRECT_ENTRY_KEY, 'true');
  } else {
    removeSessionStorageItemSafely(SSO_DIRECT_ENTRY_KEY);
  }

  const ssoTabId = ensureSsoTabId();
  // Breadcrumb only — lets a later "missing required parameters" callback error be
  // correlated back to the tab/time that started it, even when the callback only fails once.
  Logger.info('SSO started', { provider, ssoTabId });

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // codeVerifier is the one value the callback can't recover without — losing it here guarantees
  // failure after a full external IdP round-trip, so fail fast instead of redirecting at all.
  if (!setSessionStorageItemSafely(SSO_CODE_VERIFIER_KEY, codeVerifier)) {
    Logger.error('Cannot start SSO: sessionStorage unavailable', { provider });
    redirectSsoError('access_denied', 'sso_storage_unavailable', window.location.pathname);
    return;
  }

  setSessionStorageItemSafely(SSO_RETURN_PATH_KEY, window.location.pathname);

  const url = new URL(`${config.authServiceBaseUrl}/auth/web/sso/${provider}/start`, window.location.origin);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');

  // href (not replace) only for an interactive click, so Back returns to the
  // visible sign-in/sign-up page instead of skipping past it.
  if (options.interactive) {
    window.location.href = url.toString();
  } else {
    window.location.replace(url.toString());
  }
};

export const navigateToGoogleSSO = () => startSso('google', { interactive: true });
export const navigateToXeroSSO = () => startSso('xero', { interactive: true });
export const navigateToIntuitSSO = () => startSso('intuit', { interactive: true });

/**
 * Handles an SSO failure. For a direct-entry flow (sso_provider was in the
 * URL and startSso was called with isDirectEntry: true), bounces back
 * through /oauth/authorize with &error=&error_description= appended instead
 * of showing an error on /sign-in — the caller doesn't control auth-app's
 * UI, so a banner there is invisible to them. auth-app never decides for
 * itself whether an external redirect_uri is safe to navigate to: it only
 * ever replays the already-sanitized (same-origin) stored /oauth/authorize
 * URL, which validates client_id/redirect_uri against the registered-client
 * allowlist before relaying the error externally. Falls back to the
 * pre-existing `${fallbackReturnPath}?error=${errorDescription}` behavior
 * for interactive flows, or when there's no valid stored URL to replay.
 */
export const redirectSsoError = (error: 'access_denied' | 'invalid_request', errorDescription: string, fallbackReturnPath: string) => {
  const isDirectEntry = getSessionStorageItemSafely(SSO_DIRECT_ENTRY_KEY) === 'true';
  removeSessionStorageItemSafely(SSO_DIRECT_ENTRY_KEY);

  if (isDirectEntry) {
    const storedAuthorizeUrl = peekStoredAuthorizeUrl();

    if (storedAuthorizeUrl) {
      const target = new URL(storedAuthorizeUrl, window.location.origin);
      target.searchParams.set('error', error);
      target.searchParams.set('error_description', errorDescription);
      window.location.replace(target.toString());
      return;
    }
  }

  window.location.replace(`${fallbackReturnPath}?error=${errorDescription}`);
};
